import { Button, Skeleton } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";

import { useListTemplates } from "../../../api/generated/endpoints/generation/generation";
import type { TemplateCard } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { TemplateCover } from "../../../ui/TemplateCover";
import home from "../home2.module.css";
import cards from "../templates/templates.module.css";
import classes from "./simple.module.css";

/** Every available template in one read: the catalog holds fewer than the route's page of 100. */
const READ = { available: true, limit: 100 } as const;

interface TemplateCardsProps {
  teamId: string;
  /** Whether this page can draw the template's form (its area's forms are composed in `app/`). */
  usable: (template: TemplateCard) => boolean;
  onPick: (template: TemplateCard) => void;
}

/** Step 2 for an ad: the templates a person can run now, as Pro's Templates cards, by title. */
export function TemplateCards({ teamId, usable, onPick }: TemplateCardsProps) {
  const templates = useListTemplates(teamId, READ);
  const shown = templates.data?.items.filter((each) => each.available && !each.coming_soon && usable(each));
  return (
    <section className={classes.step} aria-labelledby="simple-template">
      <h2 id="simple-template" className={classes.stepTitle}>
        Which template?
      </h2>
      {templates.isError ? <ErrorAlert error={templates.error} /> : null}
      {shown === undefined ? (
        templates.isError ? null : <Skeleton className={classes.productsSkeleton} />
      ) : shown.length === 0 ? (
        <p className={classes.muted}>No template can be run here yet.</p>
      ) : (
        <div className={classes.templates}>
          {shown.map((template) => (
            <button
              key={template.id}
              type="button"
              className={`${home.templateCard} ${cards.open}`}
              aria-label={template.title}
              onClick={() => onPick(template)}
            >
              {/* Its preview plays, as Pro's Templates cards do (spec ui-perf/template-card-previews). */}
              <span className={home.thumb}>
                <TemplateCover template={template} className={home.thumbCover} iconClassName={home.thumbIcon} />
                <span className={cards.kind}>{template.media === "video" ? "Video template" : "Image template"}</span>
              </span>
              <b className={home.templateTitle}>{template.title}</b>
              {template.description === null ? null : (
                <span className={`${home.tiny} ${classes.templateLine}`}>{template.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/** A chosen template's form, on Simple's card as Image's and Video's are, with the way back to the list. */
export function TemplateFormCard({ onBack, children }: { onBack: () => void; children: ReactNode }) {
  return (
    <>
      <Button className={classes.back} variant="subtle" leftSection={<ArrowLeft size={16} />} onClick={onBack}>
        All templates
      </Button>
      <div className={classes.card}>{children}</div>
    </>
  );
}
